import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faStar,
  faArrowRight,
  faFilm,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import "./HomeBody.css";

const STATUS_TABS = ["All", "Pre-Production", "Production", "Post-Production"];

const GENRE_PILLS = [
  { name: "all epks", label: "All Projects" },
  { name: "Movie",       label: "Movie" },
  { name: "TV Show",     label: "TV Show" },
  { name: "Web Series",  label: "Web Series" },
  { name: "Documentary", label: "Documentary" },
];

export function EpkCard({ fepk, index, isLight }) {
  const { t } = useTranslation();
  const aws = process.env.REACT_APP_AWS_URL;
  const posterUrl = `${aws}/${fepk.image_details}`;
  const href = fepk.title ? `/epk/${fepk._id}` : "/";
  const likeCount = fepk.likes?.length || 0;
  const buyCount = fepk.wishes_to_buy?.length || 0;

  const scrim = isLight
    ? "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.50) 50%, transparent 100%)"
    : "linear-gradient(to top, rgba(12,0,24,0.97) 0%, rgba(12,0,24,0.65) 50%, transparent 100%)";

  return (
    <Link
      to={href}
      className="kk-dark-section epk-card tw-group tw-relative tw-block tw-overflow-hidden tw-rounded-xl tw-bg-[#12001f] tw-no-underline tw-border tw-border-white/5 hover:tw-border-[#FF43A7]/40 tw-transition-all tw-duration-300 hover:tw-shadow-[0_8px_30px_rgba(255,67,167,0.18)]"
      style={{ aspectRatio: "2/3", animationDelay: `${index * 0.035}s` }}
    >
      {/* Poster image */}
      <img
        src={posterUrl}
        alt={fepk.title}
        loading="lazy"
        className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
      />

      {/* Always-on gradient scrim */}
      <div
        className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-2/3 tw-pointer-events-none"
        style={{ background: scrim }}
      />

      {/* Production type badge */}
      {fepk.production_type && (
        <span className="tw-absolute tw-top-3 tw-left-3 tw-rounded-full tw-bg-[#FF43A7]/90 tw-px-2.5 tw-py-0.5 tw-text-[9px] tw-font-bold tw-text-white tw-uppercase tw-tracking-wide tw-backdrop-blur-sm">
          {fepk.production_type}
        </span>
      )}

      {/* Status badge */}
      {fepk.status && fepk.status !== "All" && (
        <span className="tw-absolute tw-top-3 tw-right-3 tw-rounded-full tw-bg-black/50 tw-px-2 tw-py-0.5 tw-text-[9px] tw-text-[#E2BDC9] tw-backdrop-blur-sm tw-border tw-border-[#5A3F49]/40">
          {t(fepk.status)}
        </span>
      )}

      {/* Bottom content */}
      <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-p-3">
        <h3 className="tw-text-white tw-font-bold tw-text-sm tw-leading-snug tw-m-0 tw-line-clamp-2">
          {fepk.title}
        </h3>

        {/* Hover reveal */}
        <div className="tw-overflow-hidden tw-max-h-0 group-hover:tw-max-h-28 tw-transition-all tw-duration-300 tw-ease-in-out">
          {fepk.logLine_short && (
            <p className="tw-text-[#E2BDC9] tw-text-[11px] tw-mt-2 tw-m-0 tw-leading-relaxed tw-line-clamp-2">
              {fepk.logLine_short}
            </p>
          )}
          <div className="tw-flex tw-items-center tw-justify-between tw-mt-3">
            <div className="tw-flex tw-items-center tw-gap-3 tw-text-[11px] tw-text-[#E2BDC9]">
              {likeCount > 0 && (
                <span className="tw-flex tw-items-center tw-gap-1">
                  <FontAwesomeIcon icon={faHeart} className="tw-text-[#FF43A7]" />
                  {likeCount}
                </span>
              )}
              {buyCount > 0 && (
                <span className="tw-flex tw-items-center tw-gap-1">
                  <FontAwesomeIcon icon={faStar} className="tw-text-[#9B59F5]" />
                  {buyCount}
                </span>
              )}
            </div>
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide">
              {t("View EPK")}
              <FontAwesomeIcon icon={faArrowRight} className="tw-text-[8px]" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function StatsBar({ data }) {
  const { t } = useTranslation();

  const total      = data.length;
  const active     = data.filter((e) => e.status === "Production").length;
  const genreCount = new Set(data.map((e) => e.production_type).filter(Boolean)).size;

  const stats = [
    { value: total,      label: t("Projects") },
    { value: active,     label: t("In Production") },
    { value: genreCount, label: t("Genres") },
  ];

  return (
    <div className="tw-flex tw-justify-center tw-gap-10 md:tw-gap-20 tw-py-5 tw-border-b tw-border-white/5">
      {stats.map((stat, i) => (
        <div
          key={i}
          className="stat-item tw-text-center"
          style={{ animationDelay: `${i * 0.1}s` }}
        >
          <div className="tw-text-2xl md:tw-text-3xl tw-font-extrabold tw-text-white tw-tabular-nums">
            {stat.value}
          </div>
          <div className="tw-text-[10px] tw-uppercase tw-tracking-widest tw-text-[#FF43A7] tw-mt-1 tw-font-medium">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
}

const HomeBody = ({ data = [] }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [activeStatus, setActiveStatus] = useState("All");
  const [activeGenre,  setActiveGenre]  = useState("all epks");

  const visibleData = useMemo(() => data.filter((e) => e.image_details), [data]);

  const filteredEPKs = useMemo(() => {
    let result = visibleData;
    if (activeStatus !== "All") {
      result = result.filter((e) => e.status === activeStatus);
    }
    if (activeGenre !== "all epks") {
      result = result.filter((e) => e.production_type === activeGenre);
    }
    return result;
  }, [visibleData, activeStatus, activeGenre]);

  return (
    <div className="home">
      <StatsBar data={visibleData} />

      {/* Filter bar */}
      <div className="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-3 tw-flex tw-flex-col tw-gap-3">

        {/* Status tabs */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto no-scrollbar">
          {STATUS_TABS.map((status) => (
            <button
              key={status}
              onClick={() => setActiveStatus(status)}
              className={`tw-shrink-0 tw-rounded-full tw-px-4 tw-py-1.5 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-border-none tw-cursor-pointer tw-transition-all tw-duration-200 ${
                activeStatus === status
                  ? "tw-bg-[#FF43A7] tw-text-white tw-shadow-[0_0_12px_rgba(255,67,167,0.4)]"
                  : "tw-bg-white/5 tw-text-[#E2BDC9] hover:tw-bg-white/10 hover:tw-text-white"
              }`}
            >
              {t(status)}
            </button>
          ))}
        </div>

        {/* Genre pills */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto no-scrollbar">
          {GENRE_PILLS.map((genre) => (
            <button
              key={genre.name}
              onClick={() => setActiveGenre(genre.name)}
              className={`tw-shrink-0 tw-rounded-full tw-px-4 tw-py-1.5 tw-text-xs tw-font-semibold tw-border tw-cursor-pointer tw-transition-all tw-duration-200 ${
                activeGenre === genre.name
                  ? `tw-border-[#9B59F5] tw-bg-[#9B59F5]/20 ${isLight ? 'tw-text-[#111827]' : 'tw-text-[#D4A0FF]'}`
                  : "tw-border-[#5A3F49]/60 tw-bg-transparent tw-text-[#E2BDC9] hover:tw-border-[#5A3F49] hover:tw-text-white"
              }`}
            >
              {t(genre.label)}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="tw-px-4 md:tw-px-8 tw-pb-14">
        {filteredEPKs.length === 0 ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-24 tw-gap-4 tw-text-[#5A3F49]">
            <FontAwesomeIcon icon={faFilm} className="tw-text-4xl" />
            <p className="tw-text-sm tw-m-0">{t("No projects match your filters.")}</p>
          </div>
        ) : (
          <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-5 xl:tw-grid-cols-6 tw-gap-3 md:tw-gap-5">
            {filteredEPKs.map((fepk, index) => (
              <EpkCard key={fepk._id} fepk={fepk} index={index} isLight={isLight} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeBody;
